import { BRANDS, CATEGORIES } from "@/data/catalog";

const STOP = new Set(["the", "and", "for", "with", "a", "an", "of", "in", "by", "to", "all"]);

// Extra phrases (singular) that refer to a category. Category names are added automatically.
const CATEGORY_ALIASES = {
  "air-handlers": ["air handler", "ahu"],
  "air-conditioner-condensers": ["air conditioner condenser", "ac condenser", "air conditioner", "ac unit"],
  "heat-pump-condensers": ["heat pump condenser"],
  furnaces: ["furnace", "gas furnace"],
  coils: ["coil", "evaporator coil", "evap coil"],
  "gas-electric-packaged-units": ["gas electric packaged unit", "gas pack", "gas electric"],
  "heat-pump-packaged-units": ["heat pump packaged unit", "packaged heat pump"],
  "ac-only-packaged-units": ["ac only packaged unit"],
  "single-zone-systems": ["single zone system", "single zone", "mini split", "minisplit", "ductless", "ductless mini split"],
  "multi-zone-systems": ["multi zone system", "multi zone", "multizone", "mini split", "minisplit", "ductless", "ductless mini split"],
  "mini-vrf-outdoor-units": ["mini vrf outdoor unit", "vrf outdoor"],
  "mini-vrf-indoor-units": ["mini vrf indoor unit", "vrf indoor"],
};

export const norm = (s) => String(s || "").toLowerCase().replace(/(\d)\.(\d)/g, "$1p$2").replace(/[^a-z0-9]+/g, " ").trim();
const compact = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const singular = (w) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w);
const singularPhrase = (s) => norm(s).split(" ").map(singular).join(" ");

const CATEGORY_PHRASES = CATEGORIES.flatMap((c) =>
  [c.name, ...(CATEGORY_ALIASES[c.slug] || [])].map((p) => ({ slug: c.slug, phrase: singularPhrase(p) }))
).sort((a, b) => b.phrase.length - a.phrase.length);

// Parse a free-text query into brand / category intents plus leftover terms.
export function parseQuery(raw) {
  let text = ` ${singularPhrase(raw)} `;
  const brands = [];
  BRANDS.forEach((b) => {
    const key = ` ${norm(b.name)} `;
    if (text.includes(key)) {
      brands.push(b.slug);
      text = text.replace(key, " ");
    }
  });
  const categories = [];
  const used = new Set();
  CATEGORY_PHRASES.forEach(({ slug, phrase }) => {
    const key = ` ${phrase} `;
    if (used.has(phrase) || text.includes(key)) {
      if (!categories.some((c) => c.slug === slug)) categories.push({ slug, phrase });
      used.add(phrase);
      text = text.replace(key, " ");
    }
  });
  const terms = text.split(" ").filter((t) => t && !STOP.has(t));
  return { brands, categories, terms };
}

function haystack(p) {
  const s = p.specs || {};
  const cats = p.categories && p.categories.length ? p.categories : [p.categorySlug];
  const catNames = cats.map((c) => CATEGORIES.find((x) => x.slug === c)?.name);
  const brand = BRANDS.find((b) => b.slug === p.brandSlug)?.name;
  return singularPhrase([p.name, p.model, p.sku, brand, p.subcategory, s.type, s.series, s.refrigerant, s.tonnage, s.btu, ...catNames].filter(Boolean).join(" "));
}

// Returns a relevance score (> 0) when the product matches the query, otherwise 0.
export function scoreProduct(p, raw) {
  const q = String(raw || "").trim();
  if (!q) return 1;
  const cq = compact(q);
  const model = compact(p.model);
  const sku = compact(p.sku);
  const modelHit = cq.length >= 3 && (model.includes(cq) || (sku && sku.includes(cq)));
  if (modelHit && /\d/.test(cq)) return model.startsWith(cq) ? 100 : 80;
  const miss = modelHit ? 5 : 0;

  const { brands, categories, terms } = parseQuery(q);
  if (brands.length && !brands.includes(p.brandSlug)) return miss;
  const hay = haystack(p);
  const cats = p.categories && p.categories.length ? p.categories : [p.categorySlug];
  let score = brands.length ? 10 : 0;
  if (categories.length) {
    const inCat = categories.some((c) => cats.includes(c.slug));
    const inText = categories.some((c) => hay.includes(c.phrase));
    if (!inCat && !inText) return miss;
    score += inCat ? 10 : 6;
  }
  const hayCompact = compact(hay);
  for (const t of terms) {
    if (/^\d/.test(t) || t.length <= 2) {
      if (!` ${hay} `.includes(` ${t} `)) return miss;
      score += 2;
    } else if (hay.includes(t)) score += norm(p.name).includes(t) ? 3 : 2;
    else if (t.length >= 3 && hayCompact.includes(t)) score += 1;
    else return miss;
  }
  return score || 1;
}

export function searchProducts(list, raw) {
  return list
    .map((p) => ({ p, s: scoreProduct(p, raw) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .map((x) => x.p);
}

// Autocomplete payload: brand shortcuts, category shortcuts and top products.
export function suggest(list, raw, limit = 6) {
  const q = norm(raw);
  if (q.length < 1) return { brands: [], categories: [], combo: null, products: [] };
  const sq = singularPhrase(raw);
  const { brands: pb, categories: pc } = parseQuery(raw);
  const present = (slug, key) => list.some((p) => (key === "brand" ? p.brandSlug === slug : (p.categories || [p.categorySlug]).includes(slug)));
  const brands = BRANDS.filter((b) => (pb.includes(b.slug) || norm(b.name).startsWith(q)) && present(b.slug, "brand")).slice(0, 3);
  const categories = CATEGORIES.filter(
    (c) => !c.hidden && !(pb.length && pc.length) && (pc.some((x) => x.slug === c.slug) || (sq.length >= 2 && singularPhrase(c.name).includes(sq))) && present(c.slug, "category")
  ).slice(0, 3);
  const products = searchProducts(list, raw).slice(0, limit);
  const combo = pb.length === 1 && pc.length >= 1 && products.length ? { brand: pb[0], category: pc[0].slug, multi: pc.length > 1 } : null;
  return { brands, categories, combo, products };
}
